import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { adLabsAPI } from '../utils/api';

/**
 * LabSharePage — public (no login) candidate lab page via share link.
 *
 * Security design:
 *   - GET /share/:token  → validates token, returns metadata only (no Guacamole URL)
 *   - GET /share/:token/connect → mints a fresh short-lived Guacamole token on demand
 *   - Guacamole loads inside a sandboxed iframe; the connect URL is set as iframe src
 *     only (not shown in address bar or stored)
 *   - Token is never logged to console or stored in localStorage
 */

function formatHms(s) {
  if (!s || s <= 0) return '00:00:00';
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  return [h, m, sec].map(v => String(v).padStart(2, '0')).join(':');
}

export default function LabSharePage() {
  const { token } = useParams();
  const [phase, setPhase] = useState('loading'); // loading|provisioning|ready|connecting|revoked|ended|error
  const [info, setInfo] = useState(null);
  const [errMsg, setErrMsg] = useState('');
  const [timeLeft, setTimeLeft] = useState(null);
  const iframeRef = useRef(null);
  const pollRef = useRef(null);

  // ── Step 1: fetch metadata ────────────────────────────────────────────────
  useEffect(() => {
    if (!token) { setPhase('error'); setErrMsg('No token in URL.'); return; }

    const fetchMeta = async () => {
      try {
        const res = await adLabsAPI.getShare(token);
        const data = res.data;
        setInfo(data);
        setTimeLeft(data.timeRemainingSeconds);

        if (data.status === 'running') {
          setPhase('connecting'); // triggers step 2
        } else if (data.status === 'provisioning') {
          setPhase('provisioning');
          pollRef.current = setTimeout(fetchMeta, 8000);
        } else if (data.status === 'error') {
          setPhase('error');
          setErrMsg('The lab encountered an error during provisioning. Please contact the recruiter.');
        } else {
          // stopped | expired
          setPhase('ended');
        }
      } catch (err) {
        const code = err.response?.data?.code;
        if (code === 'SHARE_REVOKED') setPhase('revoked');
        else if (code === 'LAB_ENDED') { setPhase('ended'); setInfo(err.response?.data || {}); }
        else { setPhase('error'); setErrMsg(err.response?.data?.error || 'Failed to load lab.'); }
      }
    };

    fetchMeta();
    return () => { if (pollRef.current) clearTimeout(pollRef.current); };
  }, [token]);

  // ── Step 2: fetch fresh connect URL (Guacamole token minted server-side) ──
  useEffect(() => {
    if (phase !== 'connecting') return;

    const fetchConnect = async () => {
      try {
        const res = await adLabsAPI.getShareConnect(token);
        const connectUrl = res.data?.connectUrl;
        if (!connectUrl) throw new Error('No terminal URL returned from server');
        // Set iframe src directly — URL never appears in address bar or React state
        if (iframeRef.current) {
          iframeRef.current.src = connectUrl;
        }
        setPhase('ready');
      } catch (err) {
        const code = err.response?.data?.code;
        if (code === 'SHARE_REVOKED') setPhase('revoked');
        else { setPhase('error'); setErrMsg(err.response?.data?.error || 'Failed to open terminal.'); }
      }
    };

    fetchConnect();
  }, [phase, token]);

  // ── Countdown ticker ──────────────────────────────────────────────────────
  useEffect(() => {
    if (phase !== 'ready' || !timeLeft) return;
    const tick = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) { clearInterval(tick); setPhase('ended'); return 0; }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Styles ────────────────────────────────────────────────────────────────
  const lowTime = timeLeft !== null && timeLeft < 600;
  const S = {
    page: { minHeight: '100vh', background: '#f8f9fa', display: 'flex', flexDirection: 'column', fontFamily: "'Inter',sans-serif" },
    bar:  { background: '#fff', borderBottom: '1px solid #dee2e6', padding: '0.7rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 },
    logo: { fontWeight: 700, fontSize: '1.05rem', color: '#1e88e5' },
    meta: { display: 'flex', gap: '1.25rem', alignItems: 'center', fontSize: '0.82rem', color: '#6c757d' },
    timer: { fontFamily: "'JetBrains Mono',monospace", fontWeight: 700, color: lowTime ? '#dc3545' : '#28a745' },
    center: { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '2rem' },
    card: { background: '#fff', border: '1px solid #dee2e6', borderRadius: '12px', padding: '2.5rem', maxWidth: '440px', width: '100%', textAlign: 'center' },
    icon: { fontSize: '2.5rem', marginBottom: '1rem' },
    h2: { fontSize: '1.25rem', fontWeight: 700, color: '#212529', marginBottom: '0.5rem' },
    p: { fontSize: '0.9rem', color: '#6c757d', lineHeight: 1.6 },
    spinner: { width: '36px', height: '36px', border: '3px solid #dee2e6', borderTopColor: '#1e88e5', borderRadius: '50%', animation: 'sp 0.8s linear infinite', margin: '0 auto 1rem' },
    iframe: { flex: 1, width: '100%', border: 'none', display: phase === 'ready' ? 'block' : 'none' },
  };

  const Spinner = () => <div style={S.spinner} />;

  const TopBar = () => (
    <div style={S.bar}>
      <span style={S.logo}>SecHire Lab</span>
      <div style={S.meta}>
        {info?.assessmentName && <span>{info.assessmentName}</span>}
        {timeLeft !== null && phase === 'ready' && <span style={S.timer}>{formatHms(timeLeft)} remaining</span>}
      </div>
    </div>
  );

  return (
    <div style={S.page}>
      <style>{`@keyframes sp{to{transform:rotate(360deg)}}`}</style>

      {/* Always render iframe (hidden until ready) so it mounts once */}
      {(phase === 'connecting' || phase === 'ready') && (
        <>
          <TopBar />
          <iframe
            ref={iframeRef}
            title="SecHire Lab Terminal"
            style={S.iframe}
            sandbox="allow-same-origin allow-scripts allow-forms allow-popups"
          />
          {phase === 'connecting' && (
            <div style={S.center}>
              <Spinner />
              <p style={S.p}>Opening terminal...</p>
            </div>
          )}
        </>
      )}

      {phase === 'loading' && (
        <div style={S.center}><Spinner /><p style={S.p}>Verifying access...</p></div>
      )}

      {phase === 'provisioning' && (
        <>
          <TopBar />
          <div style={S.center}>
            <div style={S.card}>
              <Spinner />
              <h2 style={S.h2}>Lab is starting up</h2>
              <p style={S.p}>Your Active Directory environment is being provisioned (~2–3 minutes). This page updates automatically.</p>
            </div>
          </div>
        </>
      )}

      {phase === 'revoked' && (
        <div style={S.center}>
          <div style={S.card}>
            <div style={S.icon}>🔒</div>
            <h2 style={S.h2}>Access Revoked</h2>
            <p style={S.p}>The recruiter has paused access to this lab. Contact them to restore it.</p>
          </div>
        </div>
      )}

      {phase === 'ended' && (
        <div style={S.center}>
          <div style={S.card}>
            <div style={S.icon}>⏰</div>
            <h2 style={S.h2}>Lab Session Ended</h2>
            <p style={S.p}>This lab has expired or been stopped. Your recruiter can start a new session if needed.</p>
          </div>
        </div>
      )}

      {phase === 'error' && (
        <div style={S.center}>
          <div style={S.card}>
            <div style={S.icon}>⚠️</div>
            <h2 style={S.h2}>Link Not Found</h2>
            <p style={S.p}>{errMsg || 'This share link is invalid or has expired.'}</p>
          </div>
        </div>
      )}
    </div>
  );
}
